import { getRiskBandsMaxLetter } from "@/features/branding";
import { HubMapDisplayPoint, RiskiestSettings } from "@/features/maps";
import {
  availableExposureHazards,
  getDamageFromResponse,
  ReturnPeriod,
  Year,
  type HazardPlusTotal,
} from "@/features/pe_aldd";
import { ensureWordyRiskValue, mvarToGradedRiskValue } from "@/features/risk_bands";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { screeningFloodExposureReturnPeriod } from "@/tools/risk-bands/utils/shared";
import type { ExposureStatus } from "@/types/exposure";
import type { CollectionAsset } from "./types";

/**
 * Builds the {@link RiskiestSettings} passed to the map for a large-site run
 */
export const buildRiskiestSettings = (
  metadata: PortfolioExportJobSchema["metadata"],
  riskiestPointId: string
): RiskiestSettings | undefined => {
  if (!metadata?.riskiestPoint || !riskiestPointId) return undefined;
  const { hazard, year } = metadata.riskiestPoint;
  return { ids: [riskiestPointId], hazard, year };
};

export const findRiskiestPoint = (
  assets: CollectionAsset[],
  metadata: { hazard: HazardPlusTotal; year: Year } | undefined
): { id: string; damage: number | null } | undefined => {
  if (!metadata) return undefined;

  const { hazard, year } = metadata;
  let riskiestPoint: { id: string; damage: number | null } | undefined;

  for (const asset of assets) {
    const response = {
      result: asset.result,
      item_id: asset.uuid,
    };

    if (!riskiestPoint) {
      const damage = getDamageFromResponse({ response, hazard, year });
      riskiestPoint = { id: asset.uuid, damage };
      continue;
    }

    const damage = getDamageFromResponse({ response, hazard, year });
    if (damage !== null && (riskiestPoint.damage === null || damage > riskiestPoint.damage)) {
      riskiestPoint = { id: asset.uuid, damage };
    }
  }

  return riskiestPoint;
};

export const valueToLetter = (value: number | null, regionType?: string): string | null => {
  if (value === null) {
    return null;
  }
  const letter = mvarToGradedRiskValue(value, getRiskBandsMaxLetter(regionType));
  return ensureWordyRiskValue(letter);
};

export const getExposureStatus = (
  response: HubMapDisplayPoint["response"],
  returnPeriod: ReturnPeriod = screeningFloodExposureReturnPeriod
): ExposureStatus | "?" => {
  const exposure = response.exposures;

  if (exposure === undefined || exposure === null) return "?";
  const exposedPointFound = availableExposureHazards.find((hazard) => {
    return exposure[hazard][returnPeriod] === "exposed";
  });

  if (exposedPointFound) {
    return "exposed";
  }

  const unexposedPointFound = availableExposureHazards.find((hazard) => exposure[hazard][returnPeriod] === "unexposed");
  return unexposedPointFound ? "unexposed" : "?";
};

export const useMaxAssetCountForCollectionHome = (): number => {
  return 10000;
};
